import { Chip, Grid, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../orders/Orders.scss';
import '../orders/UpcomingOrders.scss';
import moment from 'moment/moment';
import { Link, useSearchParams } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import { CalendarToday, Edit, Email, Redeem, Schedule } from '@mui/icons-material';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { mb } from '../common/theme/mbTokens';
import { AccountData, WaitingListItemData, WaitingListService, WaitingListSortBy } from '../api';
import { CellProps, useTable } from '../table/useTable';
import { useAuth } from '../session/InternalAuthProvider';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useLanguage } from '../common/GeneralUtils';
import TimeUtils from '../utils/TimeUtils';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { CalendarView, DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import RotatingLoader from '../common/ui/RotatingLoader';
import { TABLE_PHONE_ID } from '../table/TableComponent';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { WaitingListUpdateDialog } from './WaitingListUpdateDialog';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import OrderBoxDialog from '../calendar/calendar-items/OrderBoxDialog';
import SendEmailDialog from '../calendar/dialogs/SendEmailDialog/SendEmailDialog';

export const waitingListFetcher =
  (date?: string, sortBy: WaitingListSortBy = WaitingListSortBy.DATE) =>
  async ({ search, perPage, page }: { search?: string; page?: number; perPage: number }) =>
    WaitingListService.getWaitingList(undefined, page, perPage, search, date, sortBy);

export const WaitingListTable = React.memo(() => {
  const { users } = useCalendar();
  const { t } = useTranslation();
  const { editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<WaitingListSortBy>(WaitingListSortBy.DATE);
  const [selectedWaitingListItem, setSelectedWaitingListItem] = useState<WaitingListItemData | undefined>();
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [sendEmailItem, setSendEmailItem] = useState<WaitingListItemData | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const qsDate = searchParams.get('date');
  const qsEmail = searchParams.get('email');
  const [searchDate, setSearchDate] = useState<string | undefined>(qsDate || undefined);
  const [searchEmail, setSearchEmail] = useState<string | undefined>(qsEmail || undefined);
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const language = useLanguage();
  const { shortDateFormat } = getDateFormat(account);
  const hasWaitingListEnabled = account.experiences[0].order_pages.some(
    (orderPage) => orderPage.waiting_list?.enabled === true,
  );

  const tableDetails: CellProps<WaitingListItemData>[] = [
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip title={t('edit')}>
          <span>
            <IconButton
              className="action-icon"
              onClick={() => {
                setSelectedWaitingListItem(waitingListItem);
                setIsUpdateDialogOpen(true);
              }}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'orderDate',
      alignment: 'center',
      label: (
        <Grid
          container
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => setSortBy(WaitingListSortBy.DATE)}>
          <IconButton>
            <CalendarToday sx={{ color: sortBy === WaitingListSortBy.DATE ? 'primary.main' : undefined }} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: sortBy === WaitingListSortBy.DATE ? 700 : undefined,
              color: sortBy === WaitingListSortBy.DATE ? 'primary.main' : undefined,
            }}>
            {t('waitinglist.askedDate')}
          </Typography>
        </Grid>
      ),
      render: (waitingListItem: WaitingListItemData) => {
        const momentDate = moment(waitingListItem.date);
        const calendarView = localStorage.getItem('webook-calendarview');
        // @ts-ignore
        const weekStart = momentDate.clone().startOf(calendarView === CalendarView.THREE_DAY ? 'day' : calendarView);
        return (
          <Link to={`/dashboard/calendar/${weekStart.format(DATE_FORMAT)}`}>{momentDate.format(shortDateFormat)}</Link>
        );
      },
    },
    {
      id: 'name',
      alignment: 'center',
      label: <Typography>{t('waitinglist.name')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography
          color={waitingListItem.email === searchEmail ? 'primary.main' : undefined}
          fontWeight={waitingListItem.email === searchEmail ? 700 : undefined}>
          {waitingListItem.firstname} {waitingListItem.lastname}
        </Typography>
      ),
    },
    {
      id: 'num_persons',
      alignment: 'center',
      label: <Typography>{t('waitinglist.num_persons')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => waitingListItem.persons,
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      label: <Typography>{t('waitinglist.phone')}</Typography>,
      width: 140,
    },
    {
      id: 'email',
      alignment: 'center',
      label: <Typography>{t('waitinglist.email')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography style={{ textTransform: 'none' }}>{waitingListItem.email}</Typography>
      ),
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: <Typography>{account.activity_label_singular?.[language] || ''}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <WorkshopCircle account={account} orderPageId={waitingListItem.order_page_id} language={language} withTooltip />
      ),
    },
    {
      id: 'createdat',
      alignment: 'center',
      label: (
        <Grid
          container
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => setSortBy(WaitingListSortBy.CREATED_AT)}>
          <IconButton>
            <Schedule sx={{ color: sortBy === WaitingListSortBy.CREATED_AT ? 'primary.main' : undefined }} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: sortBy === WaitingListSortBy.CREATED_AT ? 700 : undefined,
              color: sortBy === WaitingListSortBy.CREATED_AT ? 'primary.main' : undefined,
            }}>
            {t('waitinglist.createdat')}
          </Typography>
        </Grid>
      ),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>
          {TimeUtils.getLocalTime(waitingListItem.created_at, account.timezone).format(
            `${shortDateFormat} ${timeFormat}`,
          )}
        </Typography>
      ),
    },
    {
      id: 'status',
      alignment: 'center',
      label: <Typography>{t('waitinglist.statuss')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>{t(`waitinglist.status.${waitingListItem.status?.toLowerCase()}`)}</Typography>
      ),
    },
    {
      id: 'comment',
      alignment: 'center',
      width: 150,
      label: <Typography>{t('waitinglist.comment')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => {
        const comment = waitingListItem.comment || '';
        return (
          <Tooltip title={comment.length > 50 ? comment : ''} arrow>
            <Typography
              sx={{
                maxWidth: '250px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}>
              {comment.length > 50 ? `${comment.slice(0, 50)}...` : comment}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: 'lastEditedBy',
      alignment: 'center',
      label: <Typography>{t('waitinglist.lastEditedBy')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => {
        const user = users.find((user) => user.user_id === waitingListItem.last_updated_by_user_id);
        return <Typography noWrap>{user ? `${user.first_name} ${user.last_name}` : ''}</Typography>;
      },
    },
    {
      id: 'orderlink',
      alignment: 'left',
      width: 30,
      noPadding: true,
      label: <Typography>{t('vouchers.orderlink')}</Typography>,
      render: (waitingListItem: WaitingListItemData) =>
        waitingListItem.order_id ? (
          <Tooltip title={t('info')} arrow>
            <span>
              <IconButton
                onClick={() => {
                  setOrderOpen(true);
                  setOrderId(waitingListItem.order_id);
                }}>
                <ConfirmationNumber />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      id: 'redeem',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip
          title={waitingListItem.order_id ? t('waitinglist.redeemdisabled') : t('waitinglist.convertToOrder')}
          arrow>
          <span>
            <IconButton
              onClick={() =>
                openEditOrderDrawer({
                  orderPageId: waitingListItem.order_page_id,
                  waitingListItem,
                })
              }
              disabled={!!waitingListItem.order_id}>
              <Redeem />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'sendEmail',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip title={`${t('send')} ${waitingListItem.email}`} arrow>
          <span>
            <IconButton onClick={() => setSendEmailItem(waitingListItem)} disabled={!waitingListItem.email}>
              <Email />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const handleRemoveDate = () => {
    searchParams.delete('date');
    setSearchParams(searchParams);
    setSearchDate(undefined);
    setSearchEmail(undefined);
  };

  const { TableComponent, isLoadingData, mutate, data } = useTable<WaitingListItemData>({
    opId: `getWaitingList${searchDate}-${sortBy}`, // so searchrefreshes
    fetcher: waitingListFetcher(searchDate, sortBy),
    tableDetails,
    fillHeight: true,
    actionsComponent: (
      <Grid container alignItems="center" gap={2}>
        <ToggleButtonGroup value={sortBy} exclusive onChange={(_, value) => value && setSortBy(value)} size="small">
          <ToggleButton value={WaitingListSortBy.DATE}>
            <Tooltip title={t('waitinglist.sortByDate')}>
              <CalendarToday sx={{ color: sortBy === WaitingListSortBy.DATE ? 'primary.main' : undefined }} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value={WaitingListSortBy.CREATED_AT}>
            <Tooltip title={t('waitinglist.sortByCreated')}>
              <Schedule sx={{ color: sortBy === WaitingListSortBy.CREATED_AT ? 'primary.main' : undefined }} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        {searchDate && (
          <Chip
            color="warning"
            label={t('waitinglist.filteredForDate', { date: moment(searchDate).format(shortDateFormat) })}
            onDelete={handleRemoveDate}
            deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
          />
        )}
      </Grid>
    ),
  });

  if (!hasWaitingListEnabled) {
    return (
      <Grid container sx={{ p: mb.layout.cardPadding }}>
        <Typography variant={'h6'}>{t('waitinglist.notEnabled')}</Typography>
      </Grid>
    );
  }
  if (isLoadingData) {
    return (
      <Grid
        container
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Grid>
    );
  }
  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      <WaitingListUpdateDialog
        open={isUpdateDialogOpen}
        setOpen={setIsUpdateDialogOpen}
        waitingListItem={selectedWaitingListItem}
        onUpdate={(updatedItem: WaitingListItemData) => {
          const items = data?.map((item) => (item.id === updatedItem.id ? updatedItem : item)) || [];
          void mutate(items);
        }}
      />
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void mutate();
          void closeEditOrderDrawer();
        }}
      />
      <OrderBoxDialog open={orderOpen} setOpen={setOrderOpen} orderId={orderId} />
      {sendEmailItem && (
        <SendEmailDialog
          isOpen={!!sendEmailItem}
          setIsOpen={(isOpen) => {
            if (!isOpen) setSendEmailItem(undefined);
          }}
          waitingListItemId={sendEmailItem.id}
          emailAddress={sendEmailItem.email}
        />
      )}
    </Grid>
  );
});
